import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { getDiningInfo, getTerminalLabelFromSlug, terminalSlug } from "@/data/airportDining";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UtensilsCrossed, ExternalLink } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SchemaMarkup, generateFAQSchema } from "@/components/SchemaMarkup";
import { LiveWaitStrip } from "@/components/airport/LiveWaitStrip";
import { DiningTable } from "@/components/airport/DiningTable";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

const AirportDiningTerminal = () => {
  const { code, terminal } = useParams<{ code: string; terminal: string }>();
  const upper = code?.toUpperCase() ?? "";
  const lower = code?.toLowerCase() ?? "";
  const airport = airports.find((a) => a.code === upper);
  const info = getDiningInfo(upper);
  const label = info && terminal ? getTerminalLabelFromSlug(upper, terminal) : undefined;

  if (!airport || !info || !label) return <NotFound />;

  const cityName = airport.city.split(",")[0];
  const slug = terminalSlug(label);
  const url = `https://airportbuddy.app/airports/${lower}/dining/${slug}`;
  const venues = info.venues.filter((v) => v.terminal === label);
  const otherTerminals = info.terminalPages.filter((t) => t !== label);

  const faqs = [
    {
      question: `What restaurants are in ${label} at ${upper}?`,
      answer: venues.length
        ? `${label} at ${airport.name} has ${venues.map((v) => v.name).join(", ")}. ${
            venues[0].name
          } serves ${venues[0].cuisine.toLowerCase()} and opens around ${venues[0].hours.split(" - ")[0]}.`
        : `We don't currently list individual restaurants for ${label} at ${airport.name}. Check the official ${upper} dining directory for the current line-up, and see the full ${upper} dining page for options in other terminals.`,
    },
    {
      question: `Is there food after security in ${label}?`,
      answer: venues.some((v) => v.security === "post")
        ? `Yes. ${venues
            .filter((v) => v.security === "post")
            .slice(0, 4)
            .map((v) => v.name)
            .join(", ")} are all past the checkpoint in ${label}.`
        : info.preSecurityNote,
    },
    {
      question: `Which terminal at ${upper} has the best food?`,
      answer: `${info.bestTerminal}. ${info.localPick}`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{`Food in ${label} at ${upper} – Restaurants & Hours`}</title>
        <meta
          name="description"
          content={`Restaurants and bars in ${label} at ${airport.name}: what's there, where it is, and opening hours. Plus live ${upper} security wait times.`.slice(0, 158)}
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`Food in ${label} at ${upper}`} />
        <meta
          property="og:description"
          content={`Where to eat in ${label} at ${airport.name}, with hours and live security waits.`}
        />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <SchemaMarkup schema={generateFAQSchema(faqs)} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Airports", href: "/airports" },
              { label: upper, href: `/airports/${lower}` },
              { label: "Dining", href: `/airports/${lower}/dining` },
              { label, href: `/airports/${lower}/dining/${slug}` },
            ]}
          />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-3 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Food in {label} at {upper}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                {venues.length
                  ? `${venues.length} place${venues.length === 1 ? "" : "s"} to eat in ${label} at ${airport.name}, ${cityName}.`
                  : `We don't yet list individual venues for ${label}. See the full ${upper} dining page below.`}
              </p>
            </header>

            <LiveWaitStrip airportCode={upper} />

            {venues.length > 0 && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className="h-5 w-5 text-primary" aria-hidden="true" />
                    <h2 className="text-xl font-bold text-foreground">{label} restaurants</h2>
                  </div>
                  <DiningTable venues={venues} showTerminalFilter={false} />
                  <p className="text-sm text-muted-foreground border-t pt-3">
                    Vendors and hours change. Confirm on the{" "}
                    <a
                      href={info.officialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline inline-flex items-center gap-1"
                    >
                      official {upper} dining directory
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                    .
                  </p>
                </CardContent>
              </Card>
            )}

            <AppStoreCTA
              placement="dining_terminal"
              context={`${upper}-${slug}`}
              headline="Eat now or head to security?"
              subline={`Airport Buddy tracks the ${upper} line and pushes you a leave-by reminder. Free on iPhone.`}
            />

            {otherTerminals.length > 0 && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-3">
                  <h2 className="text-xl font-bold text-foreground">Other terminals at {upper}</h2>
                  <div className="flex flex-wrap gap-2">
                    {otherTerminals.map((t) => (
                      <Button
                        key={t}
                        asChild
                        variant="outline"
                        className="min-h-[44px] rounded-xl active:scale-95"
                      >
                        <Link to={`/airports/${lower}/dining/${terminalSlug(t)}`}>{t}</Link>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-4">
                <h2 className="text-xl font-bold text-foreground">Questions about {label}</h2>
                <div className="space-y-4">
                  {faqs.map((f) => (
                    <div key={f.question} className="space-y-1">
                      <h3 className="text-base font-semibold text-foreground">{f.question}</h3>
                      <p className="text-base text-muted-foreground">{f.answer}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/dining`}>All {upper} dining</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="min-h-[44px] rounded-xl active:scale-95 flex-1"
              >
                <Link to={`/airports/${lower}`}>{upper} wait times</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="min-h-[44px] rounded-xl active:scale-95 flex-1"
              >
                <Link to={`/airports/${lower}/terminals`}>{upper} terminals</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AirportDiningTerminal;
